import React, { useEffect } from 'react';
import cn from 'classnames';
import { UploadableFile } from '../../../mp/context/UploadContext/reducer';
import { useUploadContext } from '../../../mp/context/UploadContext';
import { UploadedFile } from '../../../redux/rtk';
import { IconButton } from '../../../components/button';

const isUploadableFile = (
  file: UploadableFile | UploadedFile
): file is UploadableFile => {
  return 'progress' in file && 'error' in file && 'completed' in file;
};

const isUploadedFile = (
  file: UploadableFile | UploadedFile
): file is UploadedFile => {
  return 'filename' in file;
};

const getFileName = (file: UploadableFile | UploadedFile): string => {
  if (isUploadableFile(file)) {
    return file.name;
  }
  if (isUploadedFile(file)) {
    return file.filename;
  }
  return 'Unknown file';
};

const getProgress = (file: UploadableFile | UploadedFile): number => {
  if (isUploadableFile(file)) {
    return file.progress;
  }
  return 0; // UploadedFile doesn't have progress
};

const getError = (file: UploadableFile | UploadedFile): string | null => {
  if (isUploadableFile(file)) {
    return file.error || null;
  }
  return null; // UploadedFile doesn't have error
};

const getCompleted = (file: UploadableFile | UploadedFile): boolean => {
  if (isUploadableFile(file)) {
    return file.completed || false;
  }
  return true; // UploadedFile is always completed
};

interface FileProps {
  file: UploadableFile | UploadedFile;
  className?: string;
  onClick?: () => void;
}

export const File: React.FC<FileProps> = ({ className, file, onClick }) => {
  const hasError = !!getError(file);
  const isSuccess = !!getCompleted(file) && !hasError;
  const fileName = getFileName(file);
  const progress = getProgress(file);
  const { dispatch } = useUploadContext();

  // add 1.2 second delay to hide the progress bar so animation to 100% is visible
  useEffect(() => {
    if (!(progress === 100 && !hasError)) return undefined;
    const timeoutId = setTimeout(() => {
      dispatch({
        type: 'upload/updateProgress',
        payload: { id: file.id, progress: 0 },
      });
    }, 1200);
    return () => clearTimeout(timeoutId);
  }, [progress, hasError, file.id, dispatch]);

  return (
    <div className='file-container d-flex flex-column'>
      <div
        className={cn('d-flex flex-column file', className, {
          hasError,
          isSuccess: isSuccess,
        })}
      >
        <div className='content d-flex flex-row justify-content-between align-items-center'>
          <p
            className='mb-0 mr-3 flex-grow-1 text-truncate'
            style={{ minWidth: 0 }}
          >
            {fileName}
          </p>

          {getCompleted(file) || hasError ? (
            <IconButton
              icon={
                getCompleted(file) ? 'circled-trash' : 'circled-close-filled'
              }
              onClick={onClick}
              {...(getCompleted(file) && {
                iconProps: {
                  isOriginal: isSuccess,
                },
              })}
              className={cn('px-0', {
                hasError,
              })}
            />
          ) : null}
        </div>
        {hasError ? null : (
          <div
            className={cn('progress', { isUploading: progress > 0 })}
            style={{
              width: `${progress}%`,
            }}
          />
        )}
      </div>
      {getError(file) ? (
        <p className='file-error-message'>{getError(file)}</p>
      ) : null}
    </div>
  );
};
